import "@/sentry";
import { Toaster } from "@/components/ui/sonner";
import { VlyToolbar } from "../vly-toolbar-readonly.tsx";
import { InstrumentationProvider } from "@/instrumentation.tsx";
import AuthPage from "@/pages/Auth.tsx";
//
import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useRouteError,
  Outlet,
} from "react-router";
import "./index.css";
import Landing from "./pages/Landing.tsx";
import NotFound from "./pages/NotFound.tsx";
import DashboardHome from "./pages/DashboardHome.tsx";
import Patients from "./pages/Patients.tsx";
import TelehealthVisits from "./pages/TelehealthVisits.tsx";
import TelehealthRoom from "./pages/TelehealthRoom.tsx";
import Providers from "./pages/Providers.tsx";
import Practitioners from "./pages/Practitioners.tsx";
import Organizations from "./pages/Organizations.tsx";
import Profile from "./pages/Profile.tsx";
import Caretakers from "./pages/Caretakers.tsx";
import PatientProfile from "./pages/PatientProfile.tsx";
import PatientProfileV2 from "./pages/PatientProfileV2.tsx";
import PatientDashboard from "./pages/PatientDashboard.tsx";
import PatientCarePlan from "./pages/PatientCarePlan.tsx";
import PatientManageAccess from "./pages/PatientManageAccess";
import AddPatient from "./pages/AddPatient.tsx";
import EditPatient from "./pages/EditPatient.tsx";
import PatientSupport from "./pages/PatientSupport.tsx";
import PatientDevicePage from "./pages/PatientDevicePage.tsx";
import DeviceSettings from "./pages/DeviceSettings.tsx";
import "./types/global.d.ts";
import { UserContextProvider } from "./contexts/use-auth.tsx";

import DashboardWrapper from "./pages/DashboardWrapper.tsx";
import OrgContextProvider from "./contexts/useOrg.tsx";
import ResetPassword from "./pages/ResetPassword.tsx";
import Careplan from "./pages/Careplan.tsx";
import { TcmPlanPage } from "./components/patient-v2/tcm";
import ProviderProfile from "./pages/ProviderProfile.tsx";
import OrganizationProfile from "./pages/OrganizationProfile.tsx";
import OrganizationbillingAnalysis from "./pages/OrganizationbillingAnalysis.tsx";
import CCMPatients from "./pages/CCMPatients.tsx";
import Alerts from "./pages/Alerts.tsx";
import ViewAlert from "./pages/PedingAlertsAll.tsx";
import ResolveAlert from "./pages/ResolveAlert.tsx";
import Terms from "./pages/Terms.tsx";
import PrivacyPolicy from "./pages/Privacy.tsx";
import Unsubscribe from "./pages/Unsubscribe.tsx";
import Questionconfig from "./pages/Questionconfig.tsx";
import QuestionConfigGet from "./pages/QuestionConfigGet.tsx";
import CaretakerProfile from "./pages/CaretakerProfile.tsx";
import PractitionerProfile from "./pages/PractitionerProfile.tsx";
import OptOutPatients from "./pages/OptOutPatients.tsx";
import TreatmentPlan from "./pages/TreatmentPlan.tsx";
import PandingAlertView from "./pages/PandingAlertView.tsx";
import ReportPage from "./pages/Report/ReportPage.tsx";
import Support from "./pages/Support.tsx";
import SupportTicketDetails from "./pages/SupportTicketDetails.tsx";
import Notifications from "./pages/Notifications";
import UserManagement from "./pages/UserManagement.tsx";
import ProviderManagerManagement from "./pages/ProviderManagerManagement.tsx";
import UserSwitch from "./pages/UserSwitch.tsx";
import {
  LegacyCarePlan,
  LegacyDashboard,
  LegacyNursingNotes,
  LegacyReadingCount,
  LegacyReadings,
  LegacyTreatmentPlan,
} from "./legacy";
import LegacyPlanView from "./legacy/components/LegacyPlanView";
import { AIChatPage, AI_CHAT_ROUTE } from "./ai-chat";
import { ConflictsPage } from "./conflicts";
import { isPatientRouteId, patientIdFromPath } from "@/utils/helpers";



function RouteSyncer() {
  const location = useLocation();
  useEffect(() => {
    window.parent.postMessage(
      { type: "iframe-route-change", path: location.pathname },
      "*",
    );
  }, [location.pathname]);

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (event.data?.type === "navigate") {
        if (event.data.direction === "back") window.history.back();
        if (event.data.direction === "forward") window.history.forward();
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  return null;
}

function InvalidPatientIdReporter() {
  const { pathname } = useLocation();

  useEffect(() => {
    const patientId = patientIdFromPath(pathname);
    if (!patientId || isPatientRouteId(patientId)) return;
    console.log("Invalid patient id in route", { patientId, pathname });
    Sentry.captureMessage("Invalid patient id in route", {
      level: "warning",
      fingerprint: ["invalid-patient-route-id"],
      extra: { patientId, pathname },
    });
  }, [pathname]);

  return null;
}

function RootLayout() {
  // Load Google Maps API with Places library for address autocomplete
  useEffect(() => {
    if (typeof window.google !== "undefined" && window.google?.maps?.places) return;
    const key = import.meta.env.VITE_LOCATION_KEY;
    if (!key) {
      console.warn("VITE_LOCATION_KEY not set – address autocomplete will not work.");
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => window.dispatchEvent(new Event("google-maps-places-loaded"));
    document.head.appendChild(script);
  }, []);

  return (
    <>
      <RouteSyncer />
      <InvalidPatientIdReporter />
      <Outlet />
    </>
  );
}

// React Router's data router wraps every route with its OWN implicit error
// boundary unless an errorElement is given — that boundary sits closer to
// the thrown component than InstrumentationProvider's ErrorBoundary, so it
// catches render errors first and they never reach Sentry. This is that
// errorElement: it reports to Sentry, then shows the same fallback UI.
function RouteErrorBoundary() {
  const error = useRouteError();

  useEffect(() => {
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
    );
  }, [error]);

  return (
    <div style={{ padding: 24, textAlign: "center" }}>
      <h1>Something went wrong</h1>
      <p>The page hit an unexpected error. Please refresh and try again.</p>
    </div>
  );
}

const ProtectedPaths = [
      {
        path: "/dashboard",
        element: <DashboardHome />,
      },
      {
        path: "/legacy",
        element: <LegacyDashboard />,
      },
      {
        path: "/legacy/care-plan/:patientId",
        element: <LegacyCarePlan />,
      },
      {
        path: "/legacy/care-plan/:patientId/view",
        element: <LegacyPlanView />,
      },
      {
        path: "/legacy/treatment-plan/:patientId",
        element: <LegacyTreatmentPlan />,
      },
      {
        path: "/legacy/treatment-plan/:patientId/view",
        element: <LegacyPlanView />,
      },
      {
        path: "/legacy/nursing-notes/:patientId",
        element: <LegacyNursingNotes />,
      },
      {
        path: "/legacy/readings/:patientId",
        element: <LegacyReadings />,
      },
      {
        path: "/legacy/reading-count/:patientId",
        element: <LegacyReadingCount />,
      },
      {
        path: "/conflicts",
        element: <ConflictsPage />,
        canViewByRole: ["admin", "caretaker"],
      },
      {
        path: "/notifications",
        element: <Notifications />,
        canViewByRole: ["admin", "caretaker"],
        alsoAllowIfCanAccess: "canAccessEngage",
      },
      {
        path: AI_CHAT_ROUTE,
        element: <AIChatPage />,
        canViewByRole: ["admin"],
        alsoAllowIfCanAccess: "canAccessAIChat",
      },
      {
        path: "/organizations",
        element: <Organizations />,
    permission: "canViewOrganizationsInSidebar",
      },
      {
        path: "/organizations/:id",
        element: <OrganizationProfile />,
    permission: "canViewOrganizationsInSidebar",
      },
       {
        path: "/organizations/:id/billing-analysis",
        element: <OrganizationbillingAnalysis />,
    permission: "canViewOrganizationsInSidebar",
      },
      {
        path: "/organization/:id/reports",
        element: <ReportPage />,
        permission: "canViewOrganizationsInSidebar",
      },
      {
        path: "/providers",
        element: <Providers />,
    permission: "canViewProviderInSidebar",
      },
      {
        path: "/providers/me/managers",
        element: <ProviderManagerManagement />,
        canViewByRole: ["provider"],
      },
      {
        path: "/provider/managers",
        element: <ProviderManagerManagement />,
        canViewByRole: ["provider"],
      },
      {
        path: "/providers/:id",
        element: <ProviderProfile />,
    permission: "canViewProviderInSidebar",
      },
      {
        path: "/practitioners",
        element: <Providers />,
      },
      {
        path: "/practitioners/:id",
        element: <PractitionerProfile />,
    permission: "canViewProviderInSidebar",
      },

  {
    path: "/caretakers",
    element: <Caretakers />,
    permission: "canViewCaretakerInSidebar",

  },
  {
    path: "/caretakers/:id",
    element: <CaretakerProfile />,
    permission: "canViewCaretakerInSidebar",

  },
  {
    path: "/profile",
    element: <Profile />,
  },
      {
        path: "/patients",
        element: <Patients />,
        canViewByRole: ["admin", "provider", "caretaker", "practitioner", "patient"],
        alsoAllowIfCanAccess: "canAccessPatients",
      },
      {
        path: "/telehealth",
        element: <TelehealthVisits />,
        canViewByRole: ["admin", "patient", "provider"],
      },
      {
        path: "/telehealth/room",
        element: <TelehealthRoom />,
        canViewByRole: ["admin", "provider", "caretaker", "practitioner", "patient"],
      },
      {
        path: "/patients/opt-out",
        element: <OptOutPatients />,
        canViewByRole: ["admin"],
        alsoAllowIfCanAccess: "canAccessPatients",
      },
      {
        path: "/user-management",
        element: <UserManagement />,
        canViewByRole: ["admin"],
        // alsoAllowIfCanAccess: "canAccessConfig",
      },
      {
        path: "/ccm-patients",
        element: <CCMPatients />,
        canViewByRole: ["admin", "provider", "caretaker", "practitioner"],
        alsoAllowIfCanAccess: "canAccessPrograms",
      },
      {
        path: "/alerts",
        element: <Alerts />,
        canViewByRole: ["admin", "provider", "caretaker", "practitioner"],
        alsoAllowIfCanAccess: "canAccessTriage",
      },
       {
        path: "/alerts/view/:alertId",
        element: <PandingAlertView />,
        canViewByRole: ["admin", "provider", "caretaker", "practitioner"],
        alsoAllowIfCanAccess: "canAccessTriage",
      },
      {
        path: "/alerts/view",
        element: <ViewAlert />,
        canViewByRole: ["admin", "provider", "caretaker", "practitioner"],
        alsoAllowIfCanAccess: "canAccessTriage",
      },
      {
        path: "/alerts/resolve",
        element: <ResolveAlert />,
        canViewByRole: ["admin", "provider", "caretaker", "practitioner"],
        alsoAllowIfCanAccess: "canAccessTriage",
      },
      {
        path: "/patients/add",
        element: <AddPatient isEdit={false} />,
      permission: "canAddPatients",
      },
      {
        path: "/patients/:patientId/edit",
        element: <AddPatient isEdit={true} />,
      permission: "canAddPatients",

      },
      {
        path: "/patients/:patientId",
        element: <PatientProfileV2 />,
      },
      // {
      //   path: "/patients/:patientId/v2",
      //   element: <PatientProfileV2 />,
      // },
      {
        path: "/patients/:patientId/device-settings",
        element: <DeviceSettings />,
      },
      {
        path: "/patient-dashboard",
        element: <PatientDashboard />,
    permission: 'isPatient'
      },
      {
        path: "/patient-careplan",
        element: <PatientCarePlan />,
      },
      {
        path: "/patient-careplan/:careplanId",
        element: <Careplan isEdit={false} isView={true}/>,
      },
      {
        path: "/patient-manage-access",
        element: <PatientManageAccess />,
    permission: 'isPatient'

      },
      {
        path: "/config",
        element: <QuestionConfigGet />,
        canViewByRole: ["admin"],
        alsoAllowIfCanAccess: "canAccessConfig",
      },
      {
        path: "/config/add",
        element: <Questionconfig />,
        canViewByRole: ["admin"],
        alsoAllowIfCanAccess: "canAccessConfig",
      },
      {
        path: "/config/add/:id",
        element: <Questionconfig />,
        canViewByRole: ["admin"],
        alsoAllowIfCanAccess: "canAccessConfig",
      },
      {
        path: "/patient-support",
        element: <PatientSupport />,
    permission: 'isPatient'

      },
      {
        path: "/patient-device",
        element: <PatientDevicePage />,
        permission: "isPatient",
      },
{
  path: "/careplan/:patientId",
  element: <Careplan />
},
{
  path: "/careplan/:patientId/:careplanId",
  element: <Careplan isEdit={true}/>
},

{
  path: "/tcm-plan/:patientId",
  element: <TcmPlanPage />
},
{
  path: "/tcm-plan/:patientId/:tcmPlanId",
  element: <TcmPlanPage />
},
{
  path: "/treatment-plan/:patientId",
  element: <TreatmentPlan />
},
{
  path: "/treatment-plan/:patientId/:careplanId",
  element: <TreatmentPlan isEdit={true}/>
},
{
  path: "/support",
  element: <Support />,
  canViewByRole: ["admin", "patient" , 'caretaker' ],
  alsoAllowIfCanAccess: "canAccessSupport",
},
{
  path: "/support/:id",
  element: <SupportTicketDetails />,
  canViewByRole: ["admin" , 'caretaker' ],
  alsoAllowIfCanAccess: "canAccessSupport",
},
];

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: "/",
        element: <Landing />,
      },
      {
        path: "/auth",
        element: <AuthPage redirectAfterAuth="/dashboard" />,
      },
      {
        path: "/terms",
        element: <Terms />,
      },
      {
        path: "/privacy",
        element: <PrivacyPolicy />,
      },
      {
        path: "/unsubscribe/:token",
        element: <Unsubscribe />,
      },
      {
        path : "/auth/reset-password",
        element : <ResetPassword/>
      },
      {
        path: "/user-switch",
        element: <UserSwitch />,
      },
      ...ProtectedPaths.map((val) => ({
        ...val,
        element: (
          <DashboardWrapper
            permission={val?.permission}
            canViewByRole={val?.canViewByRole}
            alsoAllowIfCanAccess={val?.alsoAllowIfCanAccess}
          >
            {val.element}
          </DashboardWrapper>
        ),
      })),
      // {
      //   path: "",
      //   element: <DashboardWrapper ProtectedPaths={ProtectedPaths} />, // contains protected logic
      //   children: ProtectedPaths,
      // },

      {
        path: "*",
        element: <NotFound />,
      },
    ],
  },
]);

createRoot(document.getElementById("root")!).render(
<>
    <VlyToolbar />
    <InstrumentationProvider>
      <UserContextProvider>
        <OrgContextProvider>
        <Toaster position="top-right" richColors theme="light"  />
        <RouterProvider router={router} />
        </OrgContextProvider>
      </UserContextProvider>
    </InstrumentationProvider>
</>

  // <StrictMode>
  //   <VlyToolbar />
  //   <InstrumentationProvider>
  //     <UserContextProvider>
  //       <OrgContextProvider>
  //       <Toaster position="top-right" />
  //       <RouterProvider router={router} />
  //       </OrgContextProvider>
  //     </UserContextProvider>
  //   </InstrumentationProvider>
  // </StrictMode>,
);


